import { Center, Loader } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { getSupabaseBrowserClient } from '@/lib/supabase/browser';

/**
 * ProtectedRoute wrapper.
 *
 * Replaces the auth redirect logic from the old Next.js middleware.ts.
 * Reads the session from the Supabase browser client and either:
 * - Renders the children if authenticated
 * - Redirects to /auth/login?redirectTo=<current path> if not
 *
 * While the initial auth check is in flight, shows a centred loader
 * to avoid flashing the login page before Supabase restores the
 * stored session.
 */
export function ProtectedRoute({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const location = useLocation();
  const [authState, setAuthState] = useState<
    'loading' | 'authenticated' | 'unauthenticated'
  >('loading');

  useEffect(() => {
    const supabase = getSupabaseBrowserClient();
    let cancelled = false;

    supabase.auth
      .getUser()
      .then(({ data, error }) => {
        if (cancelled) return;
        setAuthState(error || !data.user ? 'unauthenticated' : 'authenticated');
      })
      .catch(() => {
        if (!cancelled) setAuthState('unauthenticated');
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      setAuthState(session?.user ? 'authenticated' : 'unauthenticated');
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  if (authState === 'loading') {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (authState === 'unauthenticated') {
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?redirectTo=${redirectTo}`} replace />;
  }

  return <>{children}</>;
}
